import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Datastore } from '@freelancer/datastore';
import type {
  ActiveLanguagesCollection,
  DomainsCollection,
  LanguagesCollection,
} from '@freelancer/datastore/collections';
import { Localization } from '@freelancer/localization';
import { Location } from '@freelancer/location';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { LinkColor, LinkHoverColor } from '@freelancer/ui/link';
import { TextSize } from '@freelancer/ui/text';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LanguageSwitcherTheme } from './language-switcher.types';

@Component({
  standalone: false,
  selector: 'fl-language-switcher-link',
  template: `
    <fl-icon
      class="mr-xsmall mobile:hide"
      [name]="'ui-globe'"
      [color]="
        color === LanguageSwitcherTheme.LIGHT
          ? IconColor.LIGHT
          : IconColor.INHERIT
      "
      [useIconFont]="true"
      [size]="IconSize.SMALL"
    ></fl-icon>
    <fl-link
      flTrackingLabel="goToChooseCountry"
      [link]="'/choose-your-country'"
      [size]="TextSize.INHERIT"
      [color]="
        color === LanguageSwitcherTheme.LIGHT
          ? LinkColor.LIGHT
          : LinkColor.INHERIT
      "
      [hoverColor]="
        color === LanguageSwitcherTheme.LIGHT
          ? LinkHoverColor.LIGHT
          : LinkHoverColor.DEFAULT
      "
    >
      {{ (domainName$ | flAsync) || 'Unknown Domain' }} /
      {{ language$ | flAsync }}
    </fl-link>
  `,
  styleUrls: ['./language-switcher-link.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSwitcherLinkComponent implements OnInit {
  IconColor = IconColor;
  LinkColor = LinkColor;
  LinkHoverColor = LinkHoverColor;
  LanguageSwitcherTheme = LanguageSwitcherTheme;
  TextSize = TextSize;
  IconSize = IconSize;

  language$: Observable<string>;
  domainName$: Observable<string | undefined>;

  @Input() color: LanguageSwitcherTheme;
  @Input() isLoggedIn: boolean;

  constructor(
    private localization: Localization,
    private datastore: Datastore,
    private location: Location,
  ) {}

  ngOnInit(): void {
    const domainName = this.location.hostname;

    this.domainName$ = this.datastore
      .collection<DomainsCollection>('domains', query =>
        // slice off the `www` because the DB doesn't have it.
        // FIXME: T267853 - move the hack to the endpoint
        query.where('domainName', '==', domainName.replace(/^www\./, '')),
      )
      .valueChanges()
      .pipe(map(domains => (domains.length > 0 ? domains[0].name : undefined)));

    this.language$ = (
      this.isLoggedIn
        ? this.datastore
            .document<LanguagesCollection>(
              'languages',
              this.localization.languageCode,
            )
            .valueChanges()
        : this.datastore
            .document<ActiveLanguagesCollection>(
              'activeLanguages',
              this.localization.languageCode,
            )
            .valueChanges()
    ).pipe(map(language => language.name));
  }
}
